import React, { useState, useEffect, useRef } from "react";
import { Button, DatePicker, Select, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { API } from "../../../Config";
import { GET } from "../../../Config/ApiRequests";
import moment from "moment";

const { RangePicker } = DatePicker;
const { Option } = Select;

function MenuBar({ dateFrom, dateTo, user_id, paid_status, search, onFilter, showUserDropdown = true, showSearch = false }) {
  const [users, setUsers] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(user_id);
  const [selectedPaidStatus, setSelectedPaidStatus] = useState(paid_status);
  const [searchValue, setSearchValue] = useState(search || "");
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    if (showUserDropdown) {
      loadUsers();
    }
  }, [showUserDropdown]);

  useEffect(() => {
    if (dateFrom && dateTo) {
      setSelectedDateRange([moment(dateFrom), moment(dateTo)]);
    } else {
      setSelectedDateRange(null);
    }
    setSelectedUserId(user_id);
    // Convert "pending" back to "unpaid" for UI display
    const uiStatus = paid_status === "pending" ? "unpaid" : paid_status || "";
    setSelectedPaidStatus(uiStatus);
    setSearchValue(search || "");
  }, [dateFrom, dateTo, user_id, paid_status, search]);

  const loadUsers = async () => {
    try {
      const response = await GET(API.USERS_LIST + `/list`);
      if (response) {
        setUsers(response || []);
      }
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };

  const handleDateChange = (dates) => {
    setSelectedDateRange(dates);
    // Convert "unpaid" to "pending" for backend compatibility
    const backendStatus = selectedPaidStatus === "unpaid" ? "pending" : selectedPaidStatus || "";
    if (dates && dates.length === 2) {
      const filters = {
        dateFrom: dates[0].format("YYYY-MM-DD"),
        dateTo: dates[1].format("YYYY-MM-DD"),
        user_id: selectedUserId || "",
        paid_status: backendStatus,
        search: searchValue || "",
      };
      onFilter(filters);
    } else {
      // Clear dates if range is cleared
      const filters = {
        dateFrom: "",
        dateTo: "",
        user_id: selectedUserId || "",
        paid_status: backendStatus,
        search: searchValue || "",
      };
      onFilter(filters);
    }
  };

  const handleUserIdChange = (value) => {
    setSelectedUserId(value);
    // Convert "unpaid" to "pending" for backend compatibility
    const backendStatus = selectedPaidStatus === "unpaid" ? "pending" : selectedPaidStatus || "";
    const filters = {
      dateFrom: selectedDateRange && selectedDateRange.length === 2 
        ? selectedDateRange[0].format("YYYY-MM-DD") 
        : "",
      dateTo: selectedDateRange && selectedDateRange.length === 2 
        ? selectedDateRange[1].format("YYYY-MM-DD") 
        : "",
      user_id: value || "",
      paid_status: backendStatus,
      search: searchValue || "",
    };
    onFilter(filters);
  };

  const handlePaidStatusChange = (value) => {
    setSelectedPaidStatus(value);
    // Convert "unpaid" to "pending" for backend compatibility
    const backendStatus = value === "unpaid" ? "pending" : value || "";
    const filters = {
      dateFrom: selectedDateRange && selectedDateRange.length === 2 
        ? selectedDateRange[0].format("YYYY-MM-DD") 
        : "",
      dateTo: selectedDateRange && selectedDateRange.length === 2 
        ? selectedDateRange[1].format("YYYY-MM-DD") 
        : "",
      user_id: selectedUserId || "",
      paid_status: backendStatus,
      search: searchValue || "",
    };
    onFilter(filters);
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new timer for debounce (500ms delay)
    debounceTimerRef.current = setTimeout(() => {
      // Convert "unpaid" to "pending" for backend compatibility
      const backendStatus = selectedPaidStatus === "unpaid" ? "pending" : selectedPaidStatus || "";
      const filters = {
        dateFrom: selectedDateRange && selectedDateRange.length === 2 
          ? selectedDateRange[0].format("YYYY-MM-DD") 
          : "",
        dateTo: selectedDateRange && selectedDateRange.length === 2 
          ? selectedDateRange[1].format("YYYY-MM-DD") 
          : "",
        user_id: selectedUserId || "",
        paid_status: backendStatus,
        search: value || "",
      };
      onFilter(filters);
    }, 500); // 500ms debounce delay
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleReset = () => {
    setSelectedDateRange(null);
    setSelectedUserId("");
    setSelectedPaidStatus("");
    setSearchValue("");

    const filters = {
      dateFrom: "",
      dateTo: "",
      user_id: "",
      paid_status: "",
      search: "",
    };

    onFilter(filters);
  };

  return (
    <div style={{ padding: "20px", background: "#fff", marginBottom: "20px" }}>
      <div style={{ display: "flex", gap: "15px", flexWrap: "wrap", alignItems: "flex-end" }}>
        {showSearch && (
          <div style={{ flex: "1", minWidth: "250px" }}>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
              {showUserDropdown 
                ? "Search (Box Number, User Name)" 
                : "Search (Box Number, User Name)"}
            </label>
            <Input
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by box number or created user name"
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              allowClear
              style={{ width: "100%" }}
            />
          </div>
        )}

        <div style={{ flex: "1", minWidth: "250px" }}>
          <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
            Date Range
          </label>
          <RangePicker
            value={selectedDateRange}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
            style={{ width: "100%" }}
          />
        </div>

        {showUserDropdown && (
          <div style={{ flex: "1", minWidth: "200px" }}>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
              User
            </label>
            <Select
              value={selectedUserId || undefined}
              onChange={handleUserIdChange}
              placeholder="Select User"
              allowClear
              showSearch
              filterOption={(input, option) =>
                option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              style={{ width: "100%" }}
            >
              {users.map((user) => (
                <Option key={user.id} value={user.id.toString()}>
                  {user.name}
                </Option>
              ))}
            </Select>
          </div>
        )}

        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
            Payment Status
          </label>
          <Select
            value={selectedPaidStatus || undefined}
            onChange={handlePaidStatusChange}
            placeholder="All Status"
            allowClear
            style={{ width: "100%" }}
          >
            <Option value="unpaid">Unpaid</Option>
            <Option value="partial">Partial</Option>
          </Select>
        </div>

        <div>
          <Button onClick={handleReset}>Reset</Button>
        </div>
      </div>
    </div>
  );
}

export default MenuBar;

